import React, { useState, useEffect, useRef } from "react";

import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import { Typography, Box } from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import useSWR from "swr";

import {
  createEmergencyContact,
  getEmergencyContactById,
  getidfnamebyWorkspace,
  updateEmergencyContact,
} from "../../../Api/Services/EmergencyServices";
import { getPetNameByWorkspaceId } from "../../../Api/Services/PetServices";
import useAuth from "../../../ContextProvider/AuthProvider";
import {
  FormHeaderBox,
  FormHeaderTitle,
  Body,
  Column,
  CancelButton,
  ButtonBoxStyle,
} from "../../../Css";
import { EmergencyFromValidation } from "../../../From/Validation";
import { relationshipOptions } from "../../../Options";
import { getCountryAsOption, getStateAsOptions } from "../../../Utils";
import AutoComplete from "../../Common/AutoComplete";
import AutoCompleteAddress from "../../Common/AutoCompleteAddress";
import CustomButton from "../../Common/Button";
import FormGroup from "../../Common/FormGroup";
import FormLayout from "../../Common/FormHeader";
import InputField from "../../Common/InputField";
import { showSnackbar } from "../../Common/Snackbar";
import "react-phone-input-2/lib/style.css";
import "./form.css";
import { useTheme } from "@mui/material/styles";
import PhoneInput from "../../../../Components/Common/PhoneInput";
import styled from "styled-components";

const defaultValue = {
  zip_code: "",
  street_address: "",
  street_address_2: "",
  apt_city: "",
  state: "",
  country: "",
  email: "",
  phone_number: "",
  phone_number_code: "",
  first_name: "",
  last_name: "",
  relationship: "",
  secondary_number: "",
  secondary_number_code: "",
  user_id: "",
  pet_id: "",
};

export default function EmergencyForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const componentPDF = useRef();
  
  const { workspaceId: w_id, petOwnerId: petowner_id } = useAuth();
  const [initialValue, setInitialValue] = useState(defaultValue);
  const [isUpdating, setIsUpdating] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [nameOptions, setNameOptions] = useState([]);
  const [fnameOptions, setFnameOptions] = useState([]);
  const [readonly, setReadonly] = useState(false);
  
  const formikRef = useRef(null);

  const isEditMode = Boolean(id);

  // Fetch emergency contact data for editing
  const { data: emergencyInfo = {} } = useSWR(
    isEditMode ? `emergencyData${id}` : null,
    async () => {
      try {
        const response = await getEmergencyContactById(id);
        const emData = response.data.data[0];
        
        setInitialValue({
          ...initialValue,
          user_id: petowner_id || "",
          ...emData,
        });
        
        const country = getCountryAsOption();
        setCountryOptions(country);
        const countryCode = country?.find(
          (country) => country.label === emData.country
        );
        const state = getStateAsOptions(countryCode?.isoCode);
        const filteredState = state?.map((item) => {
          return {
            value: item.label,
            label: item.label,
          };
        });
        filteredState.push({ value: "Other", label: "Other" });
        setStateOptions(filteredState);
        
        return response.data.data[0];
      } catch (error) {
        console.error("Error fetching emergency contact:", error);
        return null;
      }
    },
    {
      revalidateOnFocus: false,
    }
  );

  const fetchPetNames = async () => {
    try {
      const res = await getPetNameByWorkspaceId(w_id);
      const petnames = res.data.data.map((pet) => {
        return {
          value: pet?.id,
          label: pet?.name,
        };
      });
      
      if (petnames.length === 0) {
        console.log("No pet names found for user ID:", petowner_id);
      } else {
        setNameOptions(petnames);
      }
    } catch (error) {
      console.error("Error fetching pet names:", error);
    }
  };

  const fetchEmergencyNames = async () => {
    try {
      const res = await getidfnamebyWorkspace(w_id);
      let emvalues = [];
      
      if (res?.data?.data) {
        emvalues = res?.data?.data?.map((item) => {
          return {
            value: item?.id,
            label: item?.first_name || "",
          };
        });
      }
      
      emvalues?.push({
        value: "Add a new first name",
        label: "Add a new first name",
      });
      
      if (emvalues?.length === 0) {
        console.log("No names found");
      } else {
        setFnameOptions(emvalues);
      }
    } catch (error) {
      console.error("Error fetching emergency names:", error);
    }
  };

  useEffect(() => {
    fetchPetNames();
    fetchEmergencyNames();
  }, [w_id]);

  useEffect(() => {
    const country = getCountryAsOption();
    setCountryOptions(country);
  }, []);

  const handlefname = async (e, newvalue) => {
    const selectedValue = newvalue?.value;

    if (selectedValue === "Add a new first name") {
      setInitialValue({
        ...formikRef?.current?.values,
        zip_code: "",
        street_address: "",
        street_address_2: "",
        apt_city: "",
        state: "",
        country: "",
        email: "",
        phone_number: "",
        first_name: "",
        last_name: "",
        secondary_number: "",
      });
      setReadonly(false);
    } else {
      setReadonly(true);
      try {
        const res = await getEmergencyContactById(selectedValue);
        const data = res.data.data[0];
        if (!data) {
          setInitialValue(defaultValue);
        } else {
          formikRef.current.setFieldValue("first_name", data?.first_name);
          setInitialValue({
            ...formikRef?.current?.values,
            user_id: petowner_id || "",
            ...data,
          });
        }
      } catch (err) {
        console.error("Error fetching emergency contact:", err);
      }
    }
  };

  const createEmergencyData = async (values) => {
    try {
      const data = {
        ...values,
        first_name: values?.first_name,
        w_id: w_id,
      };
      const res = await createEmergencyContact(data);
      return res;
    } catch (err) {
      console.error("Error creating emergency contact:", err);
      throw err;
    }
  };

  const updateEmergencyData = async (values) => {
    try {
      const data = {
        ...values,
        first_name: values?.first_name,
        w_id: w_id,
      };
      const res = await updateEmergencyContact(id, data);
      return res;
    } catch (err) {
      console.error("Error updating emergency contact:", err);
      throw err;
    }
  };

  const handleSubmit = async (values) => {
    setIsUpdating(true);
    try {
      if (isEditMode) {
        await updateEmergencyData(values);
        showSnackbar({
          type: "success",
          message: "Emergency contact updated successfully.",
        });
      } else {
        await createEmergencyData(values);
        showSnackbar({
          type: "success",
          message: "Emergency contact added successfully.",
        });
      }
    } catch (err) {
      showSnackbar({
        type: "error",
        message: isEditMode ? "Emergency contact update failed." : "Emergency contact add failed.",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCountryChange = (e, newValue) => {
    const state = getStateAsOptions(newValue?.isoCode);
    const filteredState = state?.map((item) => {
      return {
        value: item.label,
        label: item.label,
      };
    });
    filteredState.push({ value: "Other", label: "Other" });
    setStateOptions(filteredState);
    formikRef?.current?.setFieldValue("state", "");
  };

  const handlePrint = useReactToPrint({
    content: () => componentPDF.current,
    documentTitle: "Emergency Contact Information",
    pageStyle: `
      @page {
        size: A3;
        margin: 200px;
      }
      body {
        margin: 100px;
        padding: 10px;
      }
    `,
    printStyle: `
      .print-text-field {
        font-size: 14px;
        padding: 10px;
        margin-bottom: 20px;
      }
    `,
  });

  const Country = styled.div`
    .react-tel-input .form-control {
      padding-top: 0 !important;
      border: 1px solid ${theme.palette.primary.main} !important;
      border-radius: 6px !important;
      width: ${(props) => props.width || "100% !important"};
      background: #FFF !important;
    }

    .react-tel-input :disabled {
      border: 1px solid rgba(0, 0, 0, 0.26) !important;
    }

    @media (max-width: 1220px) {
      .react-tel-input .form-control {
        width: 100% !important;
      }
    }
    @media (max-width: 900px) {
      .react-tel-input .form-control {
        width: 100% !important;
      }
    }
    @media (max-width: 600px) {
      .react-tel-input .form-control {
        width: 100% !important;
      }
    }

    .react-tel-input .flag-dropdown {
      top: 0;
      bottom: 0;
      padding: 0;
      background-color: transparent !important;
      border: transparent !important;
      border-radius: 3px 0 0 3px;
    }

    input:-internal-autofill-selected {
      background-image: none !important;
      background: rgba(255, 255, 255, 0.2) !important;
      color: fieldtext !important;
    }
  `;

  return (
    <FormLayout title={isEditMode ? "Edit Emergency Contact Information" : "Emergency Contact Information"}>
      <Formik
        initialValues={initialValue}
        innerRef={formikRef}
        onSubmit={handleSubmit}
        validationSchema={EmergencyFromValidation}
        enableReinitialize
        validateOnMount={false}
      >
        {({ getFieldProps, values, setFieldValue, isSubmitting }) => (
          <Form>
            <Box sx={FormHeaderBox(theme.palette.primary.main)}>
              <div ref={componentPDF} className="scroll2" style={{ width: "100%" }}>
                <Box sx={{ borderBottom: `1px solid ${theme.palette.primary.main}`, width: "100%" }}>
                  <Typography variant="h6" sx={FormHeaderTitle}>
                    Emergency Contact Details
                  </Typography>
                </Box>
                <Body style={{ gap: "10px 20px",marginTop: 5 }}>
                  <Column>
                    <FormGroup name="pet_id" label={"Pet Name"} requiredField>
                      <AutoComplete
                        {...getFieldProps("pet_id")}
                        name="pet_id"
                        options={nameOptions}
                        placeholder="Select a Pet Name"
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="relationship"
                      label={"Relationship"}
                      requiredField
                    >
                      <AutoComplete
                        {...getFieldProps("relationship")}
                        name="relationship"
                        options={relationshipOptions}
                        placeholder="Select a Relationship"
                        otherType="Other"
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="first_name" label="First Name" requiredField>
                      <AutoComplete
                        freeSolo
                        {...getFieldProps("first_name")}
                        options={fnameOptions}
                        placeholder="Select a First Name"
                        otherType="Add a new first name"
                        isOnChange
                        onChange={handlefname}
                        onInputChange={(e, value) =>
                          setFieldValue("first_name", value)
                        }
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="last_name" label={"Last Name"} requiredField>
                      <InputField
                        {...getFieldProps("last_name")}
                        InputProps={{
                          readOnly: readonly,
                        }}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="phone_number"
                      label={"Primary Phone Number"}
                      requiredField
                    >
                      <Country>
                        <PhoneInput
                          dialCodeName="phone_code"
                          name="phone_number"
                          onChange={(value, country) => {                          
                            setFieldValue("phone_number", value);
                          }}
                        />
                      </Country>
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="secondary_number"
                      label={"Secondary Phone Number"}
                    >
                      <Country>
                        <PhoneInput
                          dialCodeName="phone_code"
                          name="secondary_number"
                          onChange={(value, country) => {
                            setFieldValue("secondary_number", value);
                          }}
                        />
                      </Country>
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="email" label={"Email Address"} requiredField>
                      <InputField
                        {...getFieldProps("email")}
                        InputProps={{
                          readOnly: readonly,
                        }}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="street_address" label={"Street Address 1"}>
                      <AutoCompleteAddress
                        formikRef={formikRef}
                        setFieldValue={setFieldValue}
                        value={values?.street_address}
                        address="street_address"
                        city="apt_city"
                        state="state"
                        zipcode="zip_code"
                        country="country"
                        InputProps={{
                          readOnly: readonly,
                        }}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="street_address_2" label={"Street Address 2"}>
                      <InputField
                        {...getFieldProps("street_address_2")}
                        InputProps={{
                          readOnly: readonly,
                        }}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="country" label={"Country"}>
                      <AutoComplete
                        {...getFieldProps("country")}
                        name="country"
                        options={countryOptions}
                        placeholder="Select a country"
                        onChange={handleCountryChange}
                        readOnly={readonly}
                        isOnChange
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="state" label={"State"}>
                      <AutoComplete
                        {...getFieldProps("state")}
                        name="state"
                        otherType="Other"
                        options={stateOptions}
                        placeholder={"Select a type"}
                        readOnly={readonly}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="apt_city" label={"City"}>
                      <InputField
                        {...getFieldProps("apt_city")}
                        InputProps={{
                          readOnly: readonly,
                        }}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="zip_code" label={"Zip Code"}>
                      <InputField
                        {...getFieldProps("zip_code")}
                        InputProps={{
                          readOnly: readonly,
                        }}
                      />
                    </FormGroup>
                  </Column>
                </Body>
              </div>
              <Box sx={ButtonBoxStyle(`${theme.palette.primary.main}`)}>
                <Box display="flex">
                  <CustomButton
                    label="Cancel"
                    type="button"
                    style={CancelButton(`${theme.palette.primary.main}`)}
                    onClick={() => navigate(-1)}
                  />
                  <CustomButton
                    label={isSubmitting || isUpdating ? (isEditMode ? "Saving..." : "Submitting...") : (isEditMode ? "Save" : "Submit")}
                    disabled={isSubmitting || isUpdating}
                    type="submit"
                  />
                  {isEditMode && (
                    <CustomButton
                      onClick={handlePrint}
                      startIcon={<LocalPrintshopOutlinedIcon />}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </FormLayout>
  );
}
